import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowLeft, Save } from 'lucide-react';
import { useForm, type Resolver } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import {
  criarPessoaFisicaSchema,
  criarPessoaJuridicaSchema,
  ESTADOS_CIVIS,
  NOME_ESTADO_CIVIL,
  NOME_NATUREZA,
  NOME_REGIME_BENS,
  NOME_SEXO,
  REGIMES_BENS,
  SEXOS,
  type CriarPessoaDto,
  type Natureza,
  type Pessoa,
} from '@social/shared';
import { PaginaShell } from '../componentes/pagina-shell';
import { Aviso, Botao, Campo, Entrada, Selecao } from '../componentes/ui';
import { api } from '../lib/sessao';
import { ErroApi } from '../lib/api';
import { useState } from 'react';

/*
  Cadastro de pessoa — o PRIMEIRO formulário do produto.

  `react-hook-form` e `@hookform/resolvers` já estavam nas dependências e nunca
  tinham sido usados; o que este arquivo faz, portanto, é fixar o padrão que o
  cadastro de sociedade vai herdar. Três decisões, com o porquê:

  1. **A validação é o schema COMPARTILHADO** (`criarPessoaFisicaSchema`), pelo
     `zodResolver`. Não há uma segunda lista de regras no front: o que o
     servidor recusa, a tela recusa antes, com a mesma mensagem — e quando a
     regra mudar, muda num lugar só.

  2. **Uma seção por pergunta**, não um paredão de vinte campos. Quem cadastra
     está lendo um documento (RG, contrato social) e preenche em blocos.

  3. **O que é condicional some**, não fica desabilitado: regime de bens só
     existe para quem é casado (art. 977 do Código Civil), e um campo apagado
     no meio do formulário é ruído que a pessoa tenta preencher.
*/

/** As seções do formulário viram `<fieldset>` de verdade, com legenda. */
function Secao({
  titulo,
  descricao,
  children,
}: {
  titulo: string;
  descricao?: string;
  children: React.ReactNode;
}) {
  return (
    <fieldset className="border-tinta-100 rounded-xl border p-4">
      <legend className="text-tinta-900 px-2 text-sm font-semibold">{titulo}</legend>
      {descricao && <p className="text-tinta-500 mb-3 text-xs">{descricao}</p>}
      {/* Duas colunas a partir de `sm`, uma no celular — ver a regra de grade
          da skill `social-desenho`: `grid-cols-1` explícito na base. */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">{children}</div>
    </fieldset>
  );
}

/** Ocupa a linha inteira da grade — para nome, razão social e logradouro. */
function LinhaCheia({ children }: { children: React.ReactNode }) {
  return <div className="sm:col-span-2">{children}</div>;
}

export function PessoaNova(): JSX.Element {
  const [natureza, setNatureza] = useState<Natureza>('FISICA');

  return (
    <PaginaShell
      titulo="Nova pessoa"
      subtitulo="Quem vai assinar como sócio, administrador, procurador ou testemunha"
      acoes={
        <Link
          to="/pessoas"
          className="border-tinta-100 bg-campo text-tinta-900 hover:border-marca-600 hover:bg-marca-600/10 inline-flex h-11 items-center gap-2 rounded-lg border px-3 text-sm whitespace-nowrap transition-colors"
        >
          <ArrowLeft className="size-4" aria-hidden />
          Voltar
        </Link>
      }
    >
      <div className="flex flex-col gap-4">
        <Campo rotulo="Natureza" id="natureza" obrigatorio>
          <Selecao
            id="natureza"
            value={natureza}
            onChange={(e) => setNatureza(e.target.value as Natureza)}
          >
            {(['FISICA', 'JURIDICA'] as const).map((n) => (
              <option key={n} value={n}>
                {NOME_NATUREZA[n]}
              </option>
            ))}
          </Selecao>
        </Campo>

        {/*
          `key` na natureza: trocar de PF para PJ REMONTA o formulário.

          Sem isso, o `react-hook-form` guardaria os valores do formulário
          anterior — e o resolver passaria a validar contra o outro schema com
          campos que não existem mais. Remontar é mais barato e mais honesto do
          que sincronizar dois formulários à mão.
        */}
        {natureza === 'FISICA' ? (
          <FormularioFisica key="FISICA" />
        ) : (
          <FormularioJuridica key="JURIDICA" />
        )}
      </div>
    </PaginaShell>
  );
}

/** O que os dois formulários compartilham: salvar, avisar, voltar. */
/**
 * Salva — criando ou editando, conforme haja um `id`.
 *
 * Um caminho só porque as duas telas são a MESMA: o formulário de edição é o
 * de cadastro com valores preenchidos. Dois componentes separados divergiriam
 * no primeiro campo acrescentado, e o sintoma seria um campo que existe no
 * cadastro e some na correção.
 */
function useSalvarPessoa(id?: string) {
  const navegar = useNavigate();
  const fila = useQueryClient();

  return useMutation({
    mutationFn: (dto: CriarPessoaDto) =>
      id ? api.patch<Pessoa>(`/pessoas/${id}`, dto) : api.post<Pessoa>('/pessoas', dto),
    onSuccess: async () => {
      // Invalida em vez de mexer na lista à mão: a pessoa volta do servidor
      // com `id` e `codigo`, e reconstruir isso aqui duplicaria a regra do
      // contador.
      await fila.invalidateQueries({ queryKey: ['pessoas'] });
      navegar('/pessoas');
    },
  });
}

/**
 * A mensagem que a pessoa lê quando o servidor recusa.
 *
 * `ErroApi` carrega a mensagem de domínio já em português ("Este CPF já está
 * cadastrado neste escritório") — mostrar `error.message` cru de qualquer
 * `Error` traria stack ou "Failed to fetch" para a tela.
 */
function mensagemDoErro(erro: unknown): string {
  if (erro instanceof ErroApi) return erro.message;
  return 'Não foi possível salvar. Tente de novo.';
}

export type CriarFisica = Extract<CriarPessoaDto, { natureza: 'FISICA' }>;
export type CriarJuridica = Extract<CriarPessoaDto, { natureza: 'JURIDICA' }>;

export function FormularioFisica({
  inicial,
  id,
}: {
  inicial?: CriarFisica;
  id?: string;
}): JSX.Element {
  const salvar = useSalvarPessoa(id);
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<CriarFisica>({
    resolver: zodResolver(criarPessoaFisicaSchema) as Resolver<CriarFisica>,
    defaultValues: inicial ?? {
      natureza: 'FISICA',
      nacionalidade: 'brasileira',
    },
  });

  // Regime de bens só existe para quem é casado — ver o cabeçalho.
  const casado = watch('estadoCivil') === 'CASADO';

  return (
    <form
      onSubmit={handleSubmit((dto) => salvar.mutate(dto))}
      className="flex flex-col gap-4"
      noValidate
    >
      {salvar.isError && <Aviso>{mensagemDoErro(salvar.error)}</Aviso>}

      <Secao titulo="Identificação">
        <LinhaCheia>
          <Campo rotulo="Nome completo" id="nome" obrigatorio erro={errors.nome?.message}>
            <Entrada id="nome" erro={!!errors.nome} {...register('nome')} />
          </Campo>
        </LinhaCheia>

        <Campo rotulo="Sexo" id="sexo" obrigatorio erro={errors.sexo?.message}>
          <Selecao id="sexo" erro={!!errors.sexo} {...register('sexo')}>
            <option value="">Selecione…</option>
            {SEXOS.map((s) => (
              <option key={s} value={s}>
                {NOME_SEXO[s]}
              </option>
            ))}
          </Selecao>
        </Campo>

        <Campo
          rotulo="Nascimento"
          id="nascimento"
          obrigatorio
          erro={errors.nascimento?.message}
        >
          <Entrada
            id="nascimento"
            type="date"
            erro={!!errors.nascimento}
            {...register('nascimento')}
          />
        </Campo>

        <Campo
          rotulo="Nacionalidade"
          id="nacionalidade"
          obrigatorio
          erro={errors.nacionalidade?.message}
        >
          <Entrada
            id="nacionalidade"
            erro={!!errors.nacionalidade}
            {...register('nacionalidade')}
          />
        </Campo>

        <Campo
          rotulo="Profissão"
          id="profissao"
          obrigatorio
          dica="Como aparece no contrato: empresário, médico, engenheiro…"
          erro={errors.profissao?.message}
        >
          <Entrada id="profissao" erro={!!errors.profissao} {...register('profissao')} />
        </Campo>

        <Campo
          rotulo="Naturalidade"
          id="naturalidadeMunicipio"
          obrigatorio
          erro={errors.naturalidadeMunicipio?.message}
        >
          <Entrada
            id="naturalidadeMunicipio"
            erro={!!errors.naturalidadeMunicipio}
            {...register('naturalidadeMunicipio')}
          />
        </Campo>

        <Campo
          rotulo="UF de nascimento"
          id="naturalidadeUf"
          obrigatorio
          erro={errors.naturalidadeUf?.message}
        >
          <Entrada
            id="naturalidadeUf"
            maxLength={2}
            erro={!!errors.naturalidadeUf}
            {...register('naturalidadeUf')}
          />
        </Campo>
      </Secao>

      <Secao
        titulo="Estado civil"
        descricao="O regime de bens importa juridicamente: comunhão universal impede certos arranjos de quotas entre cônjuges sócios (art. 977 do Código Civil)."
      >
        <Campo
          rotulo="Estado civil"
          id="estadoCivil"
          obrigatorio
          erro={errors.estadoCivil?.message}
        >
          <Selecao
            id="estadoCivil"
            erro={!!errors.estadoCivil}
            {...register('estadoCivil')}
          >
            <option value="">Selecione…</option>
            {ESTADOS_CIVIS.map((e) => (
              <option key={e} value={e}>
                {NOME_ESTADO_CIVIL[e]}
              </option>
            ))}
          </Selecao>
        </Campo>

        {casado && (
          <Campo
            rotulo="Regime de bens"
            id="regimeBens"
            obrigatorio
            erro={errors.regimeBens?.message}
          >
            <Selecao
              id="regimeBens"
              erro={!!errors.regimeBens}
              {...register('regimeBens')}
            >
              <option value="">Selecione…</option>
              {REGIMES_BENS.map((r) => (
                <option key={r} value={r}>
                  {NOME_REGIME_BENS[r]}
                </option>
              ))}
            </Selecao>
          </Campo>
        )}
      </Secao>

      <Secao titulo="Documentos">
        <Campo
          rotulo="CPF"
          id="cpf"
          obrigatorio
          dica="Só números"
          erro={errors.cpf?.message}
        >
          <Entrada
            id="cpf"
            inputMode="numeric"
            maxLength={11}
            erro={!!errors.cpf}
            {...register('cpf')}
          />
        </Campo>

        <Campo
          rotulo="Identidade"
          id="identidadeNumero"
          obrigatorio
          erro={errors.identidadeNumero?.message}
        >
          <Entrada
            id="identidadeNumero"
            erro={!!errors.identidadeNumero}
            {...register('identidadeNumero')}
          />
        </Campo>

        <Campo
          rotulo="Órgão emissor"
          id="identidadeOrgao"
          obrigatorio
          dica="SESP-PR, SSP-SP…"
          erro={errors.identidadeOrgao?.message}
        >
          <Entrada
            id="identidadeOrgao"
            erro={!!errors.identidadeOrgao}
            {...register('identidadeOrgao')}
          />
        </Campo>
      </Secao>

      <CamposDeEndereco register={register} errors={errors} />

      <RodapeDoFormulario salvando={salvar.isPending} editando={Boolean(id)} />
    </form>
  );
}

export function FormularioJuridica({
  inicial,
  id,
}: {
  inicial?: CriarJuridica;
  id?: string;
}): JSX.Element {
  const salvar = useSalvarPessoa(id);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<CriarJuridica>({
    resolver: zodResolver(criarPessoaJuridicaSchema) as Resolver<CriarJuridica>,
    defaultValues: inicial ?? { natureza: 'JURIDICA' },
  });

  return (
    <form
      onSubmit={handleSubmit((dto) => salvar.mutate(dto))}
      className="flex flex-col gap-4"
      noValidate
    >
      {salvar.isError && <Aviso>{mensagemDoErro(salvar.error)}</Aviso>}

      <Secao
        titulo="Identificação"
        descricao="Pessoa jurídica não tem sexo, nascimento nem estado civil — só entra como sócia de outra sociedade."
      >
        <LinhaCheia>
          <Campo
            rotulo="Razão social"
            id="razaoSocial"
            obrigatorio
            erro={errors.razaoSocial?.message}
          >
            <Entrada
              id="razaoSocial"
              erro={!!errors.razaoSocial}
              {...register('razaoSocial')}
            />
          </Campo>
        </LinhaCheia>

        <Campo
          rotulo="CNPJ"
          id="cnpj"
          obrigatorio
          dica="Só números"
          erro={errors.cnpj?.message}
        >
          <Entrada
            id="cnpj"
            inputMode="numeric"
            maxLength={14}
            erro={!!errors.cnpj}
            {...register('cnpj')}
          />
        </Campo>

        <Campo
          rotulo="NIRE"
          id="nire"
          dica="Opcional — o número do registro na Junta"
          erro={errors.nire?.message}
        >
          <Entrada id="nire" erro={!!errors.nire} {...register('nire')} />
        </Campo>
      </Secao>

      <CamposDeEndereco register={register} errors={errors} />

      <RodapeDoFormulario salvando={salvar.isPending} editando={Boolean(id)} />
    </form>
  );
}

/**
 * O endereço é idêntico nas duas naturezas — `enderecoSchema` é o mesmo.
 *
 * Os tipos vêm frouxos de propósito (`any` nos genéricos do `react-hook-form`):
 * amarrá-los aos dois formulários exigiria um genérico com `Path<T>` que só
 * serve para agradar o compilador. O que garante a correção aqui é o resolver,
 * que valida o objeto inteiro contra o schema compartilhado.
 */
function CamposDeEndereco({
  register,
  errors,
}: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  register: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  errors: any;
}): JSX.Element {
  const e = errors.endereco ?? {};

  return (
    <Secao titulo="Endereço">
      <LinhaCheia>
        <Campo
          rotulo="Logradouro"
          id="endereco.logradouro"
          obrigatorio
          erro={e.logradouro?.message}
        >
          <Entrada
            id="endereco.logradouro"
            erro={!!e.logradouro}
            {...register('endereco.logradouro')}
          />
        </Campo>
      </LinhaCheia>

      <Campo rotulo="Número" id="endereco.numero" obrigatorio erro={e.numero?.message}>
        <Entrada
          id="endereco.numero"
          erro={!!e.numero}
          {...register('endereco.numero')}
        />
      </Campo>

      <Campo rotulo="Complemento" id="endereco.complemento" erro={e.complemento?.message}>
        <Entrada
          id="endereco.complemento"
          erro={!!e.complemento}
          {...register('endereco.complemento')}
        />
      </Campo>

      <Campo rotulo="Bairro" id="endereco.bairro" obrigatorio erro={e.bairro?.message}>
        <Entrada
          id="endereco.bairro"
          erro={!!e.bairro}
          {...register('endereco.bairro')}
        />
      </Campo>

      <Campo
        rotulo="Município"
        id="endereco.municipio"
        obrigatorio
        erro={e.municipio?.message}
      >
        <Entrada
          id="endereco.municipio"
          erro={!!e.municipio}
          {...register('endereco.municipio')}
        />
      </Campo>

      <Campo rotulo="UF" id="endereco.uf" obrigatorio erro={e.uf?.message}>
        <Entrada
          id="endereco.uf"
          maxLength={2}
          erro={!!e.uf}
          {...register('endereco.uf')}
        />
      </Campo>

      <Campo
        rotulo="CEP"
        id="endereco.cep"
        obrigatorio
        dica="Só números"
        erro={e.cep?.message}
      >
        <Entrada
          id="endereco.cep"
          inputMode="numeric"
          maxLength={8}
          erro={!!e.cep}
          {...register('endereco.cep')}
        />
      </Campo>
    </Secao>
  );
}

/**
 * Salvar à direita, cancelar à esquerda — e o cancelar é LINK, não botão.
 *
 * Sair do formulário é navegar, e um `<button>` que navega quebra o
 * "abrir em nova aba" e o clique do meio.
 */
function RodapeDoFormulario({
  salvando,
  editando,
}: {
  salvando: boolean;
  editando?: boolean;
}): JSX.Element {
  return (
    <div className="border-tinta-100 flex items-center justify-end gap-2 border-t pt-4">
      <Link
        to="/pessoas"
        className="text-tinta-700 hover:bg-marca-600/10 inline-flex h-11 items-center rounded-lg px-4 text-sm transition-colors"
      >
        Cancelar
      </Link>
      <Botao type="submit" carregando={salvando}>
        <Save className="size-4" aria-hidden />
        {editando ? 'Salvar alterações' : 'Salvar pessoa'}
      </Botao>
    </div>
  );
}
